import { useMemo, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/pagination';
import { AREA_TAX_LOCATION_NAME_ASC } from '../../../constants/SortConstants';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../../constants/FilterConstants';
import { useLazyGetAllSublocationsQuery } from '../../../services/Sublocation/SublocationService';
import { useSearchFilterParam } from '../../../hooks';
import {
  setActiveAreaTaxLocationFilters,
  setArchivedAreaTaxLocationFilters,
  resetActiveAreaTaxLocationFiltersState,
  resetArchivedAreaTaxLocationFiltersState,
} from '../../../redux-slice/manageAreaTaxLocationFilters';

const defaultFilterState = {
  [FILTER_VALUES.SUBLOCATION_ID]: [],
  searchText: '',
  pageNumber: DEFAULT_PAGE,
  rowsPerPage: DEFAULT_PER_PAGE,
  sort: AREA_TAX_LOCATION_NAME_ASC,
};

const useAreaTaxFilters = ({ status }) => {
  const dispatch = useDispatch();
  const { DRAWER_TYPES, TITLE, FILTER_DATA } = FILTER_CONSTANTS;

  const { activeAreaTaxLocations, archivedAreaTaxLocations } = useSelector(
    (state) => state.manageAreaTaxLocationFilters || {},
  );
  const tabFilters = (status === 'archived' ? archivedAreaTaxLocations : activeAreaTaxLocations) || defaultFilterState;

  const lastPageRef = useRef(tabFilters.pageNumber);
  const lastPerPageRef = useRef(tabFilters.rowsPerPage);

  useEffect(() => {
    lastPageRef.current = tabFilters.pageNumber;
  }, [tabFilters.pageNumber]);

  useEffect(() => {
    lastPerPageRef.current = tabFilters.rowsPerPage;
  }, [tabFilters.rowsPerPage]);

  const [getAllSublocations] = useLazyGetAllSublocationsQuery();

  const filterParams = useMemo(() => {
    const params = { ...FILTER_DATA.MANAGE_AREA_TAX_LOCATION.INITIAL_PARAMS };
    params.sublocationId = tabFilters[FILTER_VALUES.SUBLOCATION_ID] ?? [];
    return params;
  }, [tabFilters]);

  const filterData = useMemo(
    () =>
      FILTER_DATA.MANAGE_AREA_TAX_LOCATION.FILTER.map((item) => ({
        ...item,
        api: getAllSublocations,
      })),
    [getAllSublocations],
  );

  const searchFilterData = useSearchFilterParam({
    filterKey: FILTER_DATA.MANAGE_AREA_TAX_LOCATION,
    filterParams,
    searchText: tabFilters.searchText,
    sort: tabFilters.sort,
  });

  const setFilters = status === 'archived' ? setArchivedAreaTaxLocationFilters : setActiveAreaTaxLocationFilters;
  const resetFilters =
    status === 'archived' ? resetArchivedAreaTaxLocationFiltersState : resetActiveAreaTaxLocationFiltersState;

  const pageChangeHandler = useCallback(
    (currentPage) => {
      if (Number(currentPage) === Number(lastPageRef.current)) {
        return;
      }
      lastPageRef.current = currentPage;
      dispatch(
        setFilters({
          ...tabFilters,
          pageNumber: currentPage,
        }),
      );
    },
    [dispatch, setFilters, tabFilters],
  );

  const perPageChangeHandler = useCallback(
    (newPerPage) => {
      if (Number(newPerPage) === Number(lastPerPageRef.current)) {
        return;
      }
      lastPerPageRef.current = newPerPage;
      lastPageRef.current = DEFAULT_PAGE;
      dispatch(
        setFilters({
          ...tabFilters,
          rowsPerPage: newPerPage,
          pageNumber: DEFAULT_PAGE,
        }),
      );
    },
    [dispatch, setFilters, tabFilters],
  );

  const searchHandler = useCallback(
    (params) => {
      lastPageRef.current = DEFAULT_PAGE;
      dispatch(
        setFilters({
          ...tabFilters,
          searchText: params,
          pageNumber: DEFAULT_PAGE,
        }),
      );
    },
    [dispatch, setFilters, tabFilters],
  );

  const onSearchReset = useCallback(() => {
    lastPageRef.current = DEFAULT_PAGE;
    dispatch(
      setFilters({
        ...tabFilters,
        searchText: '',
        pageNumber: DEFAULT_PAGE,
      }),
    );
  }, [dispatch, setFilters, tabFilters]);

  const applyFilterHandler = useCallback(
    (filterValues) => {
      lastPageRef.current = DEFAULT_PAGE;
      dispatch(
        setFilters({
          ...tabFilters,
          ...filterValues,
          pageNumber: DEFAULT_PAGE,
        }),
      );
    },
    [dispatch, setFilters, tabFilters],
  );

  const resetFilterParams = useCallback(() => {
    lastPageRef.current = DEFAULT_PAGE;
    dispatch(resetFilters());
  }, [dispatch, resetFilters]);

  return {
    tabFilters,
    filterParams,
    filterData,
    searchFilterData,

    DRAWER_TYPES,
    TITLE,
    FILTER_DATA,

    pageChangeHandler,
    perPageChangeHandler,
    searchHandler,
    onSearchReset,
    applyFilterHandler,
    resetFilterParams,
  };
};

export default useAreaTaxFilters;
